/**
 * TabVideo Component - React 19 Migration
 *
 * Displays a paginated table of video call appointments with join functionality and navigation.
 *
 * MIGRATION CHANGES FROM REACT 16 TO 19:
 * =====================================
 *
 * 1. MATERIAL-UI v4 → MUI v5 (@mui/material)
 *    - Updated imports from '@material-ui/core' to '@mui/material'
 *    - Replaced makeStyles/withStyles with styled-components
 *    - Changed icon imports to '@mui/icons-material'
 *
 * 2. ROUTING
 *    - Replaced react-router-dom v5 useHistory with v6 useNavigate
 *    - Updated navigation syntax from history.push() to navigate()
 *
 * 3. STYLED COMPONENTS APPROACH
 *    - withStyles(theme => ({...}))(Component) → styled(Component)`...`
 *    - makeStyles hook → styled components
 *    - Better performance and cleaner code
 *
 * 4. PERFORMANCE OPTIMIZATIONS
 *    - Added useCallback for event handlers
 *    - Memoized status mapping function
 *    - Styled components created outside to prevent recreation
 *
 * 5. GLOBAL CONSTANTS
 *    - Fixed imports to use proper getter functions from globalConstants
 *    - Consistent import path
 *
 * COMPONENT FEATURES:
 * ==================
 * - Paginated table with video appointments
 * - "Join Now" button with smart timing (5 minutes before appointment)
 * - Real-time video call initiation via Teladoc API
 * - Status display with internationalization
 * - Date/time formatting based on locale
 * - Analytics tracking
 * - Error handling with dialog messages
 * - Empty state handling
 *
 * @param {Array} videoList - Array of video appointment objects
 * @param {Function} setValue - Sets the active tab index
 * @param {Function} setAppointmentId - Sets the selected appointment ID
 * @param {Function} setTypeAppointment - Sets appointment type ('video')
 * @param {Function} setGotTime - Sets the current server time
 * @param {Function} setUserType - Sets user type ('CL' or 'AB')
 */

import React, { useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TableFooter,
  TablePagination,
  IconButton,
  useTheme,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { apiService } from '../../../../../../services';
import {
  setCookie,
  trativeFormatDateWithIdiom,
} from '../../../../../../shared/helpers';
import JoinNow from './JoinNow';
import { useTranslation } from '../../../../../../shared/hooks';
import * as translations from './intl';
import {
  getLanguage,
  getPartnerId,
  getPartnerName,
  getCountryCode,
  getInstanceEnv,
} from '../../../../../../shared/constants/globalConstants';
import DialogTelemed from '../../../DialogTelemed';
import { GeneralContext } from '../../../../../../shared/contexts/GeneralContext.context';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.MuiTableCell-head': {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.main,
    fontFamily: "'Source Sans Pro'",
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '1px',
    boxShadow: '0px 1px 0px #111b1d',
  },
  '&.MuiTableCell-body': {
    fontSize: '16px',
    fontFamily: "'Source Sans Pro'",
  },
}));

/**
 * Styled Table Pagination with responsive spacing
 * Replaces: withStyles(theme => ({ [theme.breakpoints.down('sm')]: {...} }))(TablePagination)
 */
const StyledTablePagination = styled(TablePagination)(({ theme }) => ({
  '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '.MuiTablePagination-spacer': {
      display: 'none',
    },
  },
}));

/**
 * Styled Table Cell for warning messages (no bottom border)
 * Replaces: withStyles(() => ({ root: { borderBottom: 'none' } }))(TableCell)
 */
const StyledTableCellWarning = styled(TableCell)({
  borderBottom: 'none',
});

/**
 * Styled Table Row with alternating background colors
 * Replaces: withStyles(theme => ({ root: { '&:nth-of-type(odd)': {...} } }))(TableRow)
 */
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
}));

/**
 * Styled Table with minimum width
 * Replaces: makeStyles({ table: { minWidth: 700 } })
 */
const StyledTable = styled(Table)({
  minWidth: '700px',
});

/**
 * Pagination Actions Container
 * Replaces: makeStyles(theme => ({ root: { flexShrink: 0, marginLeft: theme.spacing(2.5) } }))
 */
const PaginationActionsContainer = styled('div')(({ theme }) => ({
  flexShrink: 0,
  marginLeft: theme.spacing(2.5),
}));

// =============================================================================
// PAGINATION ACTIONS COMPONENT
// =============================================================================

/**
 * Custom Pagination Actions Component
 * Provides first, previous, next, and last page navigation buttons
 */
function TablePaginationActions({
  count,
  page,
  rowsPerPage,
  onPageChange,
  labels,
}) {
  const theme = useTheme();

  const handleFirstPageButtonClick = useCallback(
    (event) => {
      onPageChange(event, 0);
    },
    [onPageChange]
  );

  const handleBackButtonClick = useCallback(
    (event) => {
      onPageChange(event, page - 1);
    },
    [onPageChange, page]
  );

  const handleNextButtonClick = useCallback(
    (event) => {
      onPageChange(event, page + 1);
    },
    [onPageChange, page]
  );

  const handleLastPageButtonClick = useCallback(
    (event) => {
      onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    },
    [onPageChange, count, rowsPerPage]
  );

  return (
    <PaginationActionsContainer>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label={labels.FIRST_PAGE_LABEL}
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label={labels.PREVIOUS_PAGE_LABEL}
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label={labels.NEXT_PAGE_LABEL}
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label={labels.LAST_PAGE_LABEL}
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </PaginationActionsContainer>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  labels: PropTypes.object.isRequired,
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * TabVideo - Video Appointments Table
 *
 * React 19 optimizations applied:
 * - useCallback for event handlers
 * - useNavigate instead of useHistory (React Router v6)
 * - Modern MUI v5 patterns
 * - Styled components for better performance
 */
function TabVideo({
  videoList,
  setValue,
  setAppointmentId,
  setTypeAppointment,
  setGotTime,
  setUserType,
}) {
  // Router navigation (React Router v6)
  const navigate = useNavigate();

  // Context and state
  const { amosToken, amosClient, adultClientToken } =
    useContext(GeneralContext);
  const [openDialog, setOpenDialog] = useState(false);
  const [configDialog, setConfigDialog] = useState({
    show: false,
    message: '',
  });
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);

  // Translations
  const { locale: intl, idiom } = useTranslation({
    translationsFile: translations,
  });

  // Destructure translations
  const {
    TITLE_APPOINTMENT_PENDING,
    TITLE_APPOINTMENT_IN_PROGRESS,
    TITLE_APPOINTMENT_DONE,
    TITLE_APPOINTMENT_CANCELED,
    TITLE_APPOINTMENT_DETAILS,
    TITLE_NO_HAVE_DOCTOR_DEFAULT_ERROR,
    TITLE_NO_HAVE_DOCTOR_MSG,
    TITLE_APPOINTMENT_NOT_DUE_YET,
    NAME,
    DATE,
    STATUS,
    FIRST_PAGE_LABEL,
    PREVIOUS_PAGE_LABEL,
    NEXT_PAGE_LABEL,
    LAST_PAGE_LABEL,
    COSTUMIZED_TABLE_LABEL,
    ROWS_PER_PAGE_LABEL,
    TEXT_NOT_APPOINTMENT,
  } = intl;

  // Calculate empty rows for consistent table height
  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, videoList.length - page * rowsPerPage);

  const onMakeStatus = useCallback(
    (typeStatus) => {
      switch (typeStatus) {
        case 'done':
          return TITLE_APPOINTMENT_DONE;
        case 'pending':
          return TITLE_APPOINTMENT_PENDING;
        case 'canceled':
          return TITLE_APPOINTMENT_CANCELED;
        case 'in-progress':
          return TITLE_APPOINTMENT_IN_PROGRESS;
        default:
          return '';
      }
    },
    [
      TITLE_APPOINTMENT_DONE,
      TITLE_APPOINTMENT_PENDING,
      TITLE_APPOINTMENT_CANCELED,
      TITLE_APPOINTMENT_IN_PROGRESS,
    ]
  );

  /**
   * Handles page change in pagination
   */
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  /**
   * Handles rows per page change
   */
  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  /**
   * Initiates a video call by authenticating with Teladoc API
   * Stores authentication tokens in cookies and navigates to video call page
   *
   * @param {string|number} videoId - The video appointment ID
   * @param {string} _userType - User type ('CL' for client, 'AB' for adult beneficiary)
   */
  const onHandleStartVideoCall = useCallback(
    async (videoId, _userType) => {
      try {
        // Select appropriate token based on user type
        const clientToken = _userType === 'CL' ? amosToken : adultClientToken;
        const bodyObj = { clientToken };

        // Call AMOS API to start video session
        const {
          data: { teladocAuthDetails },
        } = await apiService.amos.amosStartVideoCall(videoId, bodyObj);

        // Store Teladoc authentication details in cookies
        setCookie('amosTeladocToken', teladocAuthDetails.token);
        setCookie('amosTeladocSessionId', teladocAuthDetails.sessionId);
        setCookie('amosTeladocApiKey', teladocAuthDetails.apiKey);
        setCookie('amosTeladocId', videoId);

        // Navigate to video call page (React Router v6 syntax)
        navigate('?type=videoCall');
      } catch (error) {
        // Handle API errors with user-friendly messages
        const { message } = error.response.data;

        let msg = TITLE_NO_HAVE_DOCTOR_DEFAULT_ERROR;
        if (message.indexOf('150') >= 0) msg = TITLE_NO_HAVE_DOCTOR_MSG;
        if (message.indexOf('403') >= 0) msg = TITLE_APPOINTMENT_NOT_DUE_YET;

        setConfigDialog({
          show: true,
          title: '',
          message: msg,
        });
        setOpenDialog(true);
      }
    },
    [
      amosToken,
      adultClientToken,
      navigate,
      TITLE_NO_HAVE_DOCTOR_DEFAULT_ERROR,
      TITLE_NO_HAVE_DOCTOR_MSG,
      TITLE_APPOINTMENT_NOT_DUE_YET,
    ]
  );

  /**
   * Conditionally renders "Join Now" button or status text
   * Shows button only for pending/in-progress appointments within join window
   *
   * @param {Object} data - Appointment data
   * @param {string} status - Translated status text
   * @returns {JSX.Element} JoinNow component or status text
   */
  const handleShowJoinNow = useCallback(
    (data, status) => {
      const { gotTime } = data;
      if (!gotTime) return null;

      return (
        <JoinNow
          videoId={data.id}
          gotTime={gotTime}
          appointDateTime={`${data.date} ${data.time}`}
          onHandleStartVideoCall={(videoId) =>
            onHandleStartVideoCall(videoId, data.type)
          }
          status={status}
          userType={data.type}
        />
      );
    },
    [onHandleStartVideoCall]
  );

  /**
   * Handles navigation to appointment details
   * Includes analytics tracking and state updates
   */
  const handleDetails = useCallback(
    (row, event) => {
      event.stopPropagation();

      // Update parent component state
      setValue(2); // Navigate to details tab
      setAppointmentId(row.id);
      setTypeAppointment('video');
      setGotTime(row.gotTime);

      // Set user type based on client ID match
      if (row.clientId === amosClient) {
        setUserType('CL');
      } else {
        setUserType('AB');
      }

      // Analytics tracking (Google Tag Manager)
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        subsidiary_name: 'axa_partners',
        business_unit: 'customer_and_solutions',
        business_line: 'travel_solutions',
        digital_business_process: 'assistance_request',
        global_digital_asset: 'doctor_please',
        partner_id: getPartnerId(),
        partner_name: getPartnerName(),
        country: getCountryCode(),
        language: getLanguage(),
        instance_environment: getInstanceEnv(),
        user_logged: 'true',
        page_name: 'appointments details',
        appointment_type: 'video call',
        appointment_status: row.status,
        funnel_step: '4',
        step_name: 'appointment details',
        event: 'pageview',
      });
    },
    [
      setValue,
      setAppointmentId,
      setTypeAppointment,
      setGotTime,
      setUserType,
      amosClient,
    ]
  );

  /**
   * Closes error dialog
   */
  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
  }, []);

  // Get paginated rows
  const displayedRows =
    rowsPerPage > 0
      ? videoList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : videoList;

  return (
    <TableContainer component={Paper}>
      <StyledTable aria-label={COSTUMIZED_TABLE_LABEL}>
        {/* Table Header */}
        <TableHead>
          <TableRow>
            <StyledTableCell>{NAME}</StyledTableCell>
            <StyledTableCell align="left">{STATUS}</StyledTableCell>
            <StyledTableCell align="left">{DATE}</StyledTableCell>
            <StyledTableCell></StyledTableCell>
          </TableRow>
        </TableHead>

        {/* Table Body */}
        <TableBody>
          {displayedRows.map((row) => (
            <StyledTableRow
              key={row.id}
              onClick={(event) => handleDetails(row, event)}
            >
              <StyledTableCell component="th" scope="row">
                {`${row.patient.name}`}
              </StyledTableCell>

              <StyledTableCell align="left">
                {row.status === 'pending' || row.status === 'in-progress'
                  ? handleShowJoinNow(
                      row,
                      onMakeStatus(String(row.status).toLowerCase())
                    )
                  : onMakeStatus(String(row.status).toLowerCase())}
              </StyledTableCell>

              <StyledTableCell align="left">
                {`${moment(row.date).format(
                  trativeFormatDateWithIdiom(idiom)
                )} - ${row.time}`}
              </StyledTableCell>

              <StyledTableCell>
                <Button
                  color="primary"
                  endIcon={<ArrowForwardRoundedIcon />}
                  onClick={(event) => handleDetails(row, event)}
                >
                  {String(TITLE_APPOINTMENT_DETAILS).toUpperCase()}
                </Button>
              </StyledTableCell>
            </StyledTableRow>
          ))}

          {/* Empty State - Shows when no appointments exist */}
          {emptyRows === 5 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <StyledTableCellWarning colSpan={5} className="d-flex">
                <div>{TEXT_NOT_APPOINTMENT}</div>
              </StyledTableCellWarning>
            </TableRow>
          )}
        </TableBody>

        {/* Table Footer with Pagination */}
        <TableFooter>
          <TableRow>
            <StyledTablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              count={videoList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              labelRowsPerPage={ROWS_PER_PAGE_LABEL}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={(props) => (
                <TablePaginationActions
                  {...props}
                  labels={{
                    FIRST_PAGE_LABEL,
                    PREVIOUS_PAGE_LABEL,
                    NEXT_PAGE_LABEL,
                    LAST_PAGE_LABEL,
                  }}
                />
              )}
            />
          </TableRow>
        </TableFooter>
      </StyledTable>

      {/* Error Dialog */}
      <DialogTelemed
        open={openDialog}
        handleClose={handleCloseDialog}
        dialogConfig={configDialog}
      />
    </TableContainer>
  );
}

// PropTypes for runtime validation
TabVideo.propTypes = {
  videoList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      patient: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
      status: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      time: PropTypes.string.isRequired,
      gotTime: PropTypes.string,
      clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
    })
  ).isRequired,
  setValue: PropTypes.func.isRequired,
  setAppointmentId: PropTypes.func.isRequired,
  setTypeAppointment: PropTypes.func.isRequired,
  setGotTime: PropTypes.func.isRequired,
  setUserType: PropTypes.func.isRequired,
};

export default TabVideo;
